// src/modules/time-tracking/StandingJobForm.js (admin) — create/edit a standing job.
//
// A standing job is a permanent internal clock-in target that always appears in
// the crew's job picker, so they can clock in on days with no customer job.
// It is not customer work — no estimate, no lead, no price — and never appears
// in the Jobs dashboard. The GPS geofence still applies at a fixed 600 ft.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaMapMarkerAlt, FaArrowLeft, FaSave, FaSpinner, FaBuilding, FaCity, FaClock,
  FaExclamationTriangle, FaRulerHorizontal,
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Checkbox from '../../components/shared/Checkbox';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import {
  FormContainer, FormSection, FormHeader, BackButton, FormGrid, FormField, FieldLabel, SubmitButton,
} from '../../components/styled/FormComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { listStandingJobs, createStandingJob, updateStandingJob } from './timeTrackingService';

// A job anchor is always 600 ft — the backend hard-codes DEFAULT_RADIUS_FEET
// for job locations. A different radius means registering a Site instead.
const JOB_RADIUS_FEET = 600;

// Pull city/state/zip out of a Google PlaceResult's address_components.
const pickComponent = (place, type, key = 'long_name') =>
  place?.address_components?.find((c) => c.types.includes(type))?.[key] || '';

const StandingJobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    latitude: '', longitude: '', is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => {
    if (!isEditMode) return;
    const load = async () => {
      try {
        setInitialLoading(true);
        const { standingJobs } = await listStandingJobs();
        const job = (standingJobs || []).find((j) => String(j.job_id) === String(id));
        if (job) {
          setFormData({
            name: job.name || '', address: job.address || '', city: job.city || '',
            state: job.state || '', zip_code: job.zip_code || '',
            latitude: job.latitude ?? '', longitude: job.longitude ?? '',
            is_active: job.is_active,
          });
        }
      } catch (e) {
        setFeedbackMessage('Failed to load standing job.');
        setFeedbackSuccess(false); setShowFeedback(true);
      } finally { setInitialLoading(false); }
    };
    load();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  // Fill address + city/state/zip + coordinates from a Google Places selection.
  const handleAddressSelect = (address, lat, lng, place) => {
    setFormData((s) => ({
      ...s,
      address,
      city: pickComponent(place, 'locality') || s.city,
      state: pickComponent(place, 'administrative_area_level_1', 'short_name') || s.state,
      zip_code: pickComponent(place, 'postal_code') || s.zip_code,
      latitude: lat,
      longitude: lng,
    }));
    setErrors((p) => ({ ...p, address: null }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    const hasCoords = formData.latitude !== '' && formData.longitude !== '';
    const hasAddress = [formData.address, formData.city, formData.state, formData.zip_code]
      .some((v) => v && v.trim());
    if (!hasCoords && !hasAddress) {
      e.address = 'Enter an address (to geocode) or latitude/longitude — the crew has to be somewhere';
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length) { setErrors(v); return; }
    setLoading(true);
    try {
      const payload = {
        name: formData.name, address: formData.address, city: formData.city,
        state: formData.state, zip_code: formData.zip_code,
      };
      if (formData.latitude !== '' && formData.longitude !== '') {
        payload.latitude = Number(formData.latitude);
        payload.longitude = Number(formData.longitude);
      }
      if (isEditMode) {
        payload.is_active = !!formData.is_active;
        await updateStandingJob(id, payload);
        setFeedbackMessage('Standing job updated successfully');
      } else {
        await createStandingJob(payload);
        setFeedbackMessage('Standing job created — it is now in the crew’s clock-in list');
      }
      setFeedbackSuccess(true); setShowFeedback(true);
    } catch (error) {
      setFeedbackMessage(error.response?.data?.message || 'Failed to save standing job. Please try again.');
      setFeedbackSuccess(false); setShowFeedback(true);
    } finally { setLoading(false); }
  };

  if (initialLoading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/time-tracking/standing-jobs')}>
            <FaArrowLeft /> Back to Standing Jobs
          </BackButton>
          <h2>{isEditMode ? 'Edit Standing Job' : 'Add Standing Job'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3><FaMapMarkerAlt style={{ marginRight: 8 }} />Standing Job</h3>
            <FormGrid>
              <FormField index={0}>
                <Input label="Name" icon={<FaBuilding />} name="name" value={formData.name}
                  onChange={handleChange} placeholder="e.g. Shop / General Work"
                  error={errors.name} required />
                <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 4 }}>
                  This is exactly what the crew sees in the app&rsquo;s job list.
                </div>
              </FormField>
              <FormField index={1}>
                <FieldLabel>Address (search to auto-fill coordinates)</FieldLabel>
                <GooglePlaceAutocomplete
                  onSelect={handleAddressSelect}
                  initialValue={formData.address}
                  placeholder="Start typing an address…"
                />
                {errors.address && <div style={{ color: '#FF5252', fontSize: '0.85rem', marginTop: 4 }}>{errors.address}</div>}
              </FormField>
              <FormField index={2}>
                <Input label="City" icon={<FaCity />} name="city" value={formData.city}
                  onChange={handleChange} placeholder="City" />
              </FormField>
              <FormField index={3}>
                <Input label="State" name="state" value={formData.state}
                  onChange={handleChange} placeholder="MA" />
              </FormField>
              <FormField index={4}>
                <Input label="ZIP" name="zip_code" value={formData.zip_code}
                  onChange={handleChange} placeholder="01001" />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Coordinates (optional — leave blank to geocode the address)</h3>
            <FormGrid>
              <FormField index={0}>
                <Input label="Latitude" name="latitude" type="number" value={formData.latitude}
                  onChange={handleChange} placeholder="42.1234" />
              </FormField>
              <FormField index={1}>
                <Input label="Longitude" name="longitude" type="number" value={formData.longitude}
                  onChange={handleChange} placeholder="-71.1234" />
              </FormField>
            </FormGrid>
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 8 }}>
              <FaRulerHorizontal style={{ marginRight: 6 }} />
              The crew must be within {JOB_RADIUS_FEET} ft of this point. That radius is fixed for jobs —
              if you need a different one, register a Site instead and the crew can pair it with this job.
            </div>
          </FormSection>

          <FormSection>
            <h3><FaClock style={{ marginRight: 8 }} />Clock-in availability</h3>
            {isEditMode && (
              <Checkbox label="Active (shown in the crew’s clock-in list)"
                checked={!!formData.is_active}
                onChange={(e) => setFormData((s) => ({ ...s, is_active: e.target.checked }))} />
            )}
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 8, maxWidth: 620 }}>
              Normally an employee can only clock in when a job is scheduled for that day. A standing job
              is always in the list — every day, forever — so the crew can clock in when nothing is
              scheduled, without you having to invent a job for it.
              <div style={{ color: '#e0a800', marginTop: 8 }}>
                <FaExclamationTriangle style={{ marginRight: 6 }} />
                They must still be physically inside the {JOB_RADIUS_FEET} ft radius, but there is no date
                restriction — anyone on site can start a shift on any day, weekends included.
                {isEditMode
                  ? ' Untick “Active” above to switch it off when you don’t need it.'
                  : ' You can switch it off at any time from the Standing Jobs list.'}
              </div>
            </div>
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading
              ? (<><FaSpinner className="icon-spin" />Saving...</>)
              : (<><FaSave />{isEditMode ? 'Update Standing Job' : 'Create Standing Job'}</>)}
          </SubmitButton>
        </form>
      </FormContainer>

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage} success={feedbackSuccess}
        onComplete={() => { setShowFeedback(false); if (feedbackSuccess) navigate('/time-tracking/standing-jobs'); }} />
    </DashboardLayout>
  );
};

export default StandingJobForm;
